/**
 * Bloomable predictive search.
 *
 * Two data sources merged into one dropdown:
 *   1. Top-level location collections (countries, provinces, cities) embedded
 *      via Liquid in #bloomable-search-data — instant fuzzy matching, no network.
 *   2. Shopify's /search/suggest.json — products + suburb-level location collections.
 *      Suburb collections are identified by intersecting result handles with the
 *      `suburbHandles` set we embed at render time.
 *
 * Smart redirect on submit: if the typed query exactly matches a top-level
 * location title, navigate straight to /collections/<handle>. Otherwise fall
 * through to Shopify's /search?q=… results page.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 150;
  const SUGGEST_LIMIT = 6;
  const MAX_LOCATIONS_SHOWN = 6;
  const MAX_PRODUCTS_SHOWN = 6;
  const MAX_COLLECTIONS_SHOWN = 4;

  // ─── Bootstrapping ────────────────────────────────────────────────────────

  const dataEl = document.getElementById('bloomable-search-data');
  let topLocations = [];
  let suburbSet = new Set();
  let nonLocationSet = new Set();
  let featuredCollections = [];
  let topLocationsVersion = 0;
  let locationHandlePrefix = '';

  if (dataEl) {
    try {
      const data = JSON.parse(dataEl.textContent || '{}');
      suburbSet = new Set(Array.isArray(data.suburbHandles) ? data.suburbHandles : []);
      nonLocationSet = new Set(Array.isArray(data.nonLocationHandles) ? data.nonLocationHandles : []);
      featuredCollections = Array.isArray(data.featuredCollections) ? data.featuredCollections : [];
      topLocationsVersion = data.topLocationsVersion || 0;
      locationHandlePrefix = data.locationHandlePrefix || '';
      console.info('[bloomable-search] loaded ' + suburbSet.size + ' suburbs, ' + nonLocationSet.size + ' non-locations, ' + featuredCollections.length + ' featured (topLocations lazy, v' + topLocationsVersion + ', prefix "' + locationHandlePrefix + '")');
    } catch (e) {
      console.warn('[bloomable-search] Failed to parse search data', e);
    }
  } else {
    console.warn('[bloomable-search] data snippet not found on page');
  }

  // Try synchronous read from localStorage cache so first match works without waiting
  const TOP_LOCATIONS_CACHE_KEY = 'blm-search-top-locations-v1';
  try {
    const cached = JSON.parse(localStorage.getItem(TOP_LOCATIONS_CACHE_KEY) || 'null');
    if (cached && cached.version === topLocationsVersion && Array.isArray(cached.data)) {
      topLocations = cached.data;
    }
  } catch (e) { /* ignore */ }

  // Pre-compute lowercase title for fast matching
  topLocations.forEach((l) => { l._lc = (l.title || '').toLowerCase(); });
  featuredCollections.forEach((f) => { f._lc = (f.title || '').toLowerCase(); });

  // Lazy-load topLocations via Section Rendering API on first need.
  let topLocationsPromise = null;
  function ensureTopLocations() {
    if (topLocations.length > 0) return Promise.resolve(topLocations);
    if (topLocationsPromise) return topLocationsPromise;
    topLocationsPromise = fetch('/?sections=bloomable-search-top-locations')
      .then((r) => r.json())
      .then((sections) => {
        const html = sections['bloomable-search-top-locations'] || '';
        const match = html.match(/<script[^>]*id="bloomable-search-top-locations-data"[^>]*>([\s\S]*?)<\/script>/);
        if (!match) throw new Error('top-locations script not found in section response');
        const data = JSON.parse(match[1]);
        topLocations = Array.isArray(data) ? data : [];
        topLocations.forEach((l) => { l._lc = (l.title || '').toLowerCase(); });
        try {
          localStorage.setItem(TOP_LOCATIONS_CACHE_KEY, JSON.stringify({ version: topLocationsVersion, data: data }));
        } catch (e) { /* quota or disabled — ignore */ }
        console.info('[bloomable-search] lazy-loaded ' + topLocations.length + ' top-level locations');
        return topLocations;
      })
      .catch((err) => {
        console.warn('[bloomable-search] failed to lazy-load topLocations', err);
        topLocationsPromise = null; // allow retry
        return [];
      });
    return topLocationsPromise;
  }

  // Kick off the fetch on first user interaction with the search input.
  // Doesn't block first paint — runs in the background.
  let topLocationsKicked = false;
  function kickTopLocations() {
    if (topLocationsKicked) return;
    topLocationsKicked = true;
    ensureTopLocations();
  }
  document.addEventListener('focusin', (e) => {
    if (e.target && e.target.matches && e.target.matches('.bloomable-search__input, .blm-search__input')) {
      kickTopLocations();
    }
  }, true);

  function normalise(s) {
    return s.toLowerCase()
      .replace(/[‘’‚‛′‵`']/g, "'") // curly → straight apostrophe
      .replace(/[“”„‟″‶"]/g, '"')  // curly → straight quote
      .replace(/\s+/g, ' ').trim();
  }

  featuredCollections.forEach((f) => { f._norm = normalise(f.title); });

  function matchFeatured(query) {
    const q = normalise(query);
    return featuredCollections.filter((f) => f._norm.includes(q) || q.includes(f._norm));
  }

  // ─── Setup ────────────────────────────────────────────────────────────────

  let initialised = false;

  function initAll() {
    if (initialised) return;
    initialised = true;
    document.querySelectorAll('.bloomable-search__container').forEach(initOne);
    initSearchToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  function initSearchToggle() {
    // Document-level delegation: works even if Shopify re-renders the header
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-show-search-form]');
      if (trigger) {
        const wrapper = trigger.closest('.hidden-search-container');
        if (!wrapper) return;
        e.preventDefault();
        e.stopPropagation();
        const open = wrapper.classList.toggle('hidden-search-container--open');
        document.body.classList.toggle('hidden-search-container--open', open);
        document.body.classList.toggle('bloomable-search-active', open);
        if (open) {
          positionMobileSearchBar(wrapper);
          // iOS Safari only opens the on-screen keyboard for focus() calls
          // that happen synchronously inside a user-gesture handler, so call
          // it directly — no setTimeout — and call it twice (once now while
          // the gesture is still "live", once on the next frame in case the
          // element wasn't laid out yet).
          const input = wrapper.querySelector('.bloomable-search__input');
          if (input) {
            input.focus({ preventScroll: true });
            requestAnimationFrame(() => input.focus({ preventScroll: true }));
          }
        } else {
          closeWrapperFully(wrapper);
        }
        return;
      }
      // Click outside any open search wrapper closes it
      if (e.target.closest('.hidden-search-container')) return;
      if (e.target.closest('.bloomable-search__popup')) return;
      document.querySelectorAll('.hidden-search-container--open').forEach((el) => {
        el.classList.remove('hidden-search-container--open');
        closeWrapperFully(el);
      });
      document.body.classList.remove('hidden-search-container--open');
      document.body.classList.remove('bloomable-search-active');
    });

    // Reposition on resize/scroll while open
    window.addEventListener('resize', () => {
      document.querySelectorAll('.hidden-search-container--open').forEach((w) => positionMobileSearchBar(w));
    });
    window.addEventListener('scroll', () => {
      document.querySelectorAll('.hidden-search-container--open').forEach((w) => positionMobileSearchBar(w));
    }, { passive: true });
  }

  function positionMobileSearchBar(wrapper) {
    if (!window.matchMedia('(max-width: 720px)').matches) return;
    const container = wrapper.querySelector('.bloomable-search__container');
    if (!container) return;
    // Find the nearest "header" — the top toolbar that should stay visible above the search
    const headerEl =
      wrapper.closest('[data-section-type="header"]') ||
      wrapper.closest('.header-container') ||
      wrapper.closest('header') ||
      document.querySelector('[data-section-type="header"]') ||
      document.querySelector('.header-container');
    if (!headerEl) return;
    const rect = headerEl.getBoundingClientRect();
    container.style.top = Math.max(0, rect.bottom) + 'px';
  }

  function closeWrapperFully(wrapper) {
    const container = wrapper.querySelector('.bloomable-search__container');
    if (container) container.style.top = '';
    const popup = wrapper.querySelector('.bloomable-search__popup');
    if (popup) {
      popup.hidden = true;
      popup.innerHTML = '';
      popup.style.top = '';
    }
    const input = wrapper.querySelector('.bloomable-search__input');
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
  }

  function initOne(container) {
    const form = container.querySelector('form');
    const input = container.querySelector('.bloomable-search__input');
    let popup = container.querySelector('.bloomable-search__popup');

    if (!form || !input || !popup) return;

    // Replace popup with a single results container (the JS owns rendering)
    popup.innerHTML = '';
    popup.setAttribute('id', popup.id || `bloomable-search-results-${Math.random().toString(36).slice(2, 8)}`);
    popup.setAttribute('role', 'listbox');
    popup.hidden = true;

    // Portal the popup to <body> on mobile only, so it escapes the sticky-header
    // stacking context. Desktop CSS uses position:absolute relative to the search
    // container, so we leave it in place there.
    const portalToBody = () => {
      const isMobile = window.matchMedia('(max-width: 720px)').matches;
      if (isMobile) {
        if (popup.parentElement !== document.body) document.body.appendChild(popup);
      } else {
        if (popup.parentElement !== container) container.appendChild(popup);
      }
    };
    portalToBody();
    window.addEventListener('resize', portalToBody);

    // Wire combobox ARIA
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', popup.id);

    let controller = null;
    let activeIdx = -1;
    let flatItems = []; // [{href, type}]

    const debouncedSearch = debounce((q) => runSearch(q), DEBOUNCE_MS);

    input.addEventListener('input', (e) => {
      const q = e.target.value;
      if (!q.trim()) { hidePopup(); return; }
      debouncedSearch(q);
    });

    input.addEventListener('focus', (e) => {
      if (e.target.value.trim()) runSearch(e.target.value);
    });

    input.addEventListener('keydown', onKeydown);

    document.addEventListener('click', (e) => {
      if (container.contains(e.target)) return;
      if (popup.contains(e.target)) return; // popup is portaled to body
      hidePopup();
    });

    form.addEventListener('submit', (e) => {
      const q = input.value.trim();
      if (!q) return;
      const debugMatch = q.match(/^\/debug\s+(\S+)$/i);
      if (debugMatch) {
        e.preventDefault();
        window.location.href = `/search?q=%2Fdebug+${encodeURIComponent(debugMatch[1])}&type=product,collection`;
        return;
      }
      const exact = exactLocationMatch(q);
      if (exact) {
        e.preventDefault();
        window.location.href = `/collections/${exact.handle}`;
      }
      // else: let the form submit to /search?q=…
    });

    // ─── Debug mode ─────────────────────────────────────────────────────────

    function runDebug(typeFilter) {
      const validTypes = ['country', 'province', 'city', 'town', 'suburb'];
      const type = typeFilter.toLowerCase().trim();
      if (!validTypes.includes(type)) {
        popup.innerHTML = `<div class="blm-s__empty">
          <p>Unknown type <strong>${escapeHtml(type)}</strong>.</p>
          <p class="blm-s__empty-hint">Valid types: ${validTypes.join(', ')}</p>
        </div>`;
        showPopup();
        return;
      }
      const matches = type === 'suburb'
        ? Array.from(suburbSet).slice(0, 20).map((h) => ({ handle: h, title: h, type: 'suburb' }))
        : topLocations.filter((l) => l.type === type);
      flatItems = [];
      activeIdx = -1;
      if (!matches.length) {
        popup.innerHTML = `<div class="blm-s__empty"><p>No ${escapeHtml(type)} locations loaded.</p></div>`;
      } else {
        popup.innerHTML = `<div class="blm-s__section blm-s__section--locations">
          <div class="blm-s__heading"><span class="blm-s__heading-name">Debug: ${escapeHtml(type)} (${matches.length})</span></div>
          <ul class="blm-s__list">${matches.map((l) => locationItem(l)).join('')}</ul>
        </div>`;
        flatItems = Array.from(popup.querySelectorAll('[data-href]')).map((el) => ({ el, href: el.getAttribute('data-href') }));
      }
      showPopup();
    }

    // ─── Search runners ─────────────────────────────────────────────────────

    async function runSearch(q) {
      const query = q.trim();
      if (!query) { hidePopup(); return; }

      const debugMatch = query.match(/^\/debug\s+(\S+)$/i);
      if (debugMatch) { runDebug(debugMatch[1]); return; }

      // Cancel any in-flight request
      if (controller) controller.abort();
      controller = new AbortController();

      // Make sure topLocations is loaded before matching (no-op if already cached/inline)
      if (topLocations.length === 0) {
        showLoading(query);
        await ensureTopLocations();
      }

      // 1. Local locations (instant)
      const localLocations = matchLocations(query);

      // Show loading state if we don't have local hits yet
      if (!localLocations.length) showLoading(query);

      // 2. Shopify suggest (network)
      let products = [];
      let collectionsFromShopify = [];
      try {
        const data = await fetchSuggest(query, controller.signal);
        const r = (data && data.resources && data.resources.results) || {};
        products = r.products || [];
        collectionsFromShopify = r.collections || [];
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[bloomable-search] suggest failed', err);
      }

      // Categorise suggest collections via handle prefix:
      // - handle starts with locationHandlePrefix → location (use known
      //   metadata if we have it, else default to suburb)
      // - else → product/category collection
      const featuredHandleSet = new Set(featuredCollections.map((f) => f.handle));
      const topLocationsByHandle = new Map(topLocations.map((l) => [l.handle, l]));
      const seen = new Set(localLocations.map((l) => l.handle));
      const allLocations = [...localLocations];
      const productCollections = [];
      collectionsFromShopify.forEach((c) => {
        if (seen.has(c.handle)) return;
        seen.add(c.handle);
        if (featuredHandleSet.has(c.handle)) return;
        const isLocation = locationHandlePrefix && c.handle.indexOf(locationHandlePrefix) === 0;
        if (!isLocation) {
          productCollections.push(c);
          return;
        }
        const known = topLocationsByHandle.get(c.handle);
        if (known) {
          allLocations.push({
            handle: known.handle,
            title: known.title,
            type: known.type || 'location',
            parent: known.parent,
            parentTitle: known.parentTitle
          });
        } else {
          allLocations.push({ handle: c.handle, title: c.title, type: 'suburb' });
        }
      });

      render({
        query,
        featured: matchFeatured(query).slice(0, 2),
        locations: allLocations.slice(0, MAX_LOCATIONS_SHOWN),
        products: products.slice(0, MAX_PRODUCTS_SHOWN),
        collections: productCollections.slice(0, MAX_COLLECTIONS_SHOWN)
      });
    }

    function fetchSuggest(query, signal) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection',
        'resources[limit]': String(SUGGEST_LIMIT),
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,vendor,tag'
      });
      return fetch(`/search/suggest.json?${params}`, {
        signal,
        headers: { 'Accept': 'application/json' }
      }).then((r) => {
        if (!r.ok) throw new Error('suggest http ' + r.status);
        return r.json();
      });
    }

    // ─── Rendering ──────────────────────────────────────────────────────────

    function showLoading(query) {
      popup.innerHTML = `<div class="blm-s__loading">Searching for &ldquo;${escapeHtml(query)}&rdquo;…</div>`;
      showPopup();
    }

    function render({ query, featured = [], locations, products, collections }) {
      flatItems = [];
      activeIdx = -1;

      const hasAny = featured.length || locations.length || products.length || collections.length;
      if (!hasAny) {
        popup.innerHTML = `
          <div class="blm-s__empty">
            <p>No matches for &ldquo;${escapeHtml(query)}&rdquo;.</p>
            <p class="blm-s__empty-hint">Try a city, suburb, or product name.</p>
          </div>`;
        showPopup();
        return;
      }

      const parts = [];

      const q = encodeURIComponent(query);

      if (featured.length) {
        parts.push(`<div class="blm-s__section blm-s__section--featured">
          <div class="blm-s__heading">
            <span class="blm-s__heading-name">Featured</span>
          </div>
          <ul class="blm-s__list">
            ${featured.map((f) => featuredItem(f)).join('')}
          </ul>
        </div>`);
      }

      if (products.length) {
        parts.push(`<div class="blm-s__section blm-s__section--products">
          <div class="blm-s__heading">
            <span class="blm-s__heading-name">Products</span>
            <a class="blm-s__see-more" href="/search?q=${q}&type=product">See more products &rarr;</a>
          </div>
          <ul class="blm-s__list">
            ${products.map((p) => productItem(p)).join('')}
          </ul>
        </div>`);
      }

      if (locations.length) {
        parts.push(`<div class="blm-s__section blm-s__section--locations">
          <div class="blm-s__heading">
            <span class="blm-s__heading-name">Locations</span>
            <a class="blm-s__see-more" href="/search?q=${q}&type=collection">See more locations &rarr;</a>
          </div>
          <ul class="blm-s__list">
            ${locations.map((l) => locationItem(l)).join('')}
          </ul>
        </div>`);
      }

      if (collections.length) {
        parts.push(`<div class="blm-s__section blm-s__section--collections">
          <div class="blm-s__heading">
            <span class="blm-s__heading-name">Collections</span>
            <a class="blm-s__see-more" href="/search?q=${q}&type=collection">See more collections &rarr;</a>
          </div>
          <ul class="blm-s__list">
            ${collections.map((c) => collectionItem(c)).join('')}
          </ul>
        </div>`);
      }

      const seeAll = `<a class="blm-s__see-all" href="/search?q=${encodeURIComponent(query)}&type=product,collection">See all results for &ldquo;${escapeHtml(query)}&rdquo; &rarr;</a>`;
      parts.push(seeAll);

      popup.innerHTML = parts.join('');
      flatItems = Array.from(popup.querySelectorAll('[data-href]'))
        .map((el) => ({ el, href: el.getAttribute('data-href') }));

      flatItems.forEach(({ el, href }, i) => {
        el.setAttribute('role', 'option');
        el.setAttribute('id', `blm-s-opt-${i}`);
        el.addEventListener('mouseenter', () => setActive(i));
        el.addEventListener('click', (e) => {
          e.preventDefault();
          if (href) window.location.href = href;
        });
      });

      const seeAllEl = popup.querySelector('.blm-s__see-all');
      if (seeAllEl) {
        seeAllEl.setAttribute('role', 'option');
        const i = flatItems.length;
        seeAllEl.setAttribute('id', `blm-s-opt-${i}`);
        flatItems.push({ el: seeAllEl, href: seeAllEl.getAttribute('href') });
        seeAllEl.addEventListener('mouseenter', () => setActive(i));
      }

      showPopup();
    }

    function featuredItem(f) {
      const img = f.image ? `<img class="blm-s__feat-img" src="${escapeAttr(f.image)}" alt="" loading="lazy">` : '';
      return `<li class="blm-s__item blm-s__item--featured" data-href="/collections/${encodeURIComponent(f.handle)}">
        ${img}
        <span class="blm-s__feat-title">${escapeHtml(f.title)}</span>
        <span class="blm-s__feat-badge">Featured</span>
      </li>`;
    }

    function locationItem(l) {
      const typeLabel = (l.type || '').toUpperCase();
      const parent = l.parentTitle ? `<span class="blm-s__loc-parent">in ${escapeHtml(l.parentTitle)}</span>` : '';
      return `<li class="blm-s__item blm-s__item--location" data-href="/collections/${encodeURIComponent(l.handle)}">
        <span class="blm-s__loc-name">${escapeHtml(l.title)}</span>
        <span class="blm-s__loc-meta">
          ${parent}
          <span class="blm-s__loc-tag blm-s__loc-tag--${escapeAttr((l.type || 'location').toLowerCase())}">${escapeHtml(typeLabel)}</span>
        </span>
      </li>`;
    }

    function productItem(p) {
      const img = p.featured_image && p.featured_image.url
        ? p.featured_image.url
        : (p.image || '');
      const price = formatProductPrice(p);
      return `<li class="blm-s__item blm-s__item--product" data-href="${escapeAttr(p.url || ('/products/' + p.handle))}">
        ${img ? `<img class="blm-s__prod-img" src="${escapeAttr(img)}" alt="" loading="lazy">` : '<span class="blm-s__prod-img blm-s__prod-img--placeholder"></span>'}
        <span class="blm-s__prod-meta">
          <span class="blm-s__prod-name">${escapeHtml(p.title || '')}</span>
          ${price ? `<span class="blm-s__prod-price">${escapeHtml(price)}</span>` : ''}
        </span>
      </li>`;
    }

    function collectionItem(c) {
      return `<li class="blm-s__item blm-s__item--collection" data-href="/collections/${encodeURIComponent(c.handle)}">
        <span class="blm-s__col-name">${escapeHtml(c.title || '')}</span>
      </li>`;
    }

    // ─── Keyboard nav ───────────────────────────────────────────────────────

    function onKeydown(e) {
      if (e.key === 'Escape') {
        hidePopup();
        input.blur();
        return;
      }
      if (popup.hidden || !flatItems.length) {
        if (e.key === 'Enter') {
          // Let form submit handler decide
          return;
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((activeIdx + 1) % flatItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((activeIdx - 1 + flatItems.length) % flatItems.length);
      } else if (e.key === 'Enter') {
        if (activeIdx >= 0 && flatItems[activeIdx]) {
          e.preventDefault();
          window.location.href = flatItems[activeIdx].href;
        }
        // else: let form submit handler decide (smart redirect or /search)
      }
    }

    function setActive(i) {
      flatItems.forEach(({ el }, idx) => {
        el.classList.toggle('is-active', idx === i);
        if (idx === i) el.setAttribute('aria-selected', 'true');
        else el.removeAttribute('aria-selected');
      });
      activeIdx = i;
      const el = flatItems[i] && flatItems[i].el;
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', el.id);
      }
    }

    // ─── Popup state ────────────────────────────────────────────────────────

    function isMobile() {
      return window.matchMedia('(max-width: 720px)').matches;
    }

    function positionPopupForMobile() {
      if (!isMobile()) {
        popup.style.top = '';
        return;
      }
      const rect = input.getBoundingClientRect();
      popup.style.top = Math.round(rect.bottom + 4) + 'px';
    }

    function showPopup() {
      popup.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      positionPopupForMobile();
      // Mobile backdrop: only auto-add when popup is shown without an icon-toggle wrapper.
      // (Toggle-based wrappers manage the backdrop themselves on open/close.)
      const inHiddenWrapper = !!container.closest('.hidden-search-container');
      if (isMobile() && !inHiddenWrapper) {
        document.body.classList.add('bloomable-search-active');
      }
    }

    function hidePopup() {
      popup.hidden = true;
      popup.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      popup.style.top = '';
      activeIdx = -1;
      flatItems = [];
      // Only strip backdrop if we're not inside a toggle-based wrapper that's still open
      const wrapper = container.closest('.hidden-search-container');
      if (!wrapper || !wrapper.classList.contains('hidden-search-container--open')) {
        document.body.classList.remove('bloomable-search-active');
      }
      if (controller) { controller.abort(); controller = null; }
    }

    // Reposition on resize / scroll while open
    window.addEventListener('resize', () => { if (!popup.hidden) positionPopupForMobile(); });
    window.addEventListener('scroll', () => { if (!popup.hidden) positionPopupForMobile(); }, { passive: true });

    // Tap on backdrop closes (anywhere outside the search container while body has the class)
    document.addEventListener('click', (e) => {
      if (!document.body.classList.contains('bloomable-search-active')) return;
      if (container.contains(e.target)) return;
      if (e.target.closest('.bloomable-search__popup')) return;
      hidePopup();
      input.blur();
    });
  }

  // ─── Local matching ─────────────────────────────────────────────────────────

  function matchLocations(query) {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const typeRank = { country: 0, province: 1, city: 2, town: 2, suburb: 3 };
    return topLocations
      .map((l) => {
        const idx = l._lc.indexOf(q);
        if (idx === -1) return null;
        // Score: prefix match wins; then earlier match position; then type rank
        const prefixBonus = l._lc.startsWith(q) ? 0 : 1;
        const wordStartBonus = (' ' + l._lc).indexOf(' ' + q) !== -1 ? 0 : 1;
        const score = (prefixBonus * 1000) + (wordStartBonus * 500) + idx * 10 + (typeRank[l.type] ?? 9);
        return { loc: l, score };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score)
      .slice(0, MAX_LOCATIONS_SHOWN)
      .map((s) => s.loc);
  }

  function exactLocationMatch(query) {
    const q = query.toLowerCase().trim();
    return topLocations.find((l) => l._lc === q) || null;
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(s) {
    return escapeHtml(s);
  }

  function formatProductPrice(p) {
    // Shopify's suggest API returns a `price` string (e.g. "485.00") and `price_min`/`price_max`.
    // We respect what's there. If the theme has a money formatter, prefer it.
    const raw = p.price_min != null ? p.price_min : p.price;
    if (raw == null || raw === '') return '';
    const num = parseFloat(raw);
    if (Number.isNaN(num)) return '';
    // Best-effort formatting: prefix with R, two decimals, no thousands separators (keeps it short).
    return 'R ' + num.toFixed(2);
  }
})();
